import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import DropDownStyles, { DropDownStylesDark } from "../../../pages/staff/dropdownStyles";
import useDarkMode from "../../../hooks/useDarkMode";

const CountryCitySelector = ({
  selectedCountry,
  selectedCity,
  setSelectedCity,
  onChange,
  setLoading,
  selectedValueIsLabel = false,
  countryFieldName = "CompCountry",
  cityFieldName = "CompCity",
  countryLabel = "Country",
  cityLabel = "City",
  cityPlaceholder = "Select or type City",
  showCountryField = true,
  showCityField = true,
}) => {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const isDarkMode = useDarkMode();

  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const fetchCountries = useCallback(async () => {
    try {
      setLoading && setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COUNTRIES);
      const countryData = response.data.data || response.data;

      setCountries(
        countryData.map((country) => ({
          value: country.code,
          label: country.name,
        }))
      );
    } catch (err) {
      // Error fetching countries silently
    } finally {
      setLoading && setLoading(false);
    }
  }, []);

  const fetchCities = useCallback(async (countryCode) => {
    if (!countryCode) {
      setCities([]);
      return;
    }

    try {
      setLoading && setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CITIES, {
        params: { CountryCode: countryCode },
      });
      const cityData = response.data.data || response.data;

      if (cityData) {
        setCities(
          cityData.map((city) => ({
            value: selectedValueIsLabel ? city.cit_Name : city.cit_ID,
            label: city.cit_Name,
          }))
        );
      } else {
        setCities([]);
      }
    } catch (err) {
      setCities([]);
    } finally {
      setLoading && setLoading(false);
    }
  }, [selectedValueIsLabel]);

  const handleCountryChange = (option) => {
    const countryCode = option?.value || "";

    if (onChange) {
      onChange({
        [countryFieldName]: countryCode,
        [cityFieldName]: "",
      });
    }

    if (countryCode) {
      fetchCities(countryCode);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (option, actionMeta) => {
    let cityValue = "";
    if (option) {
      if (actionMeta?.action === "create-option") {
        cityValue = option.label;
      } else {
        cityValue = selectedValueIsLabel ? option.label : option.value;
      }
    }

    if (setSelectedCity) {
      setSelectedCity(cityValue);
    }

    if (onChange) {
      onChange({
        [cityFieldName]: cityValue,
      });
    }
  };

  useEffect(() => {
    if (showCountryField) {
      fetchCountries();
    }
  }, [fetchCountries, showCountryField]);

  useEffect(() => {
    if (selectedCountry) {
      fetchCities(selectedCountry);
    } else {
      setCities([]);
    }
  }, [selectedCountry, fetchCities]);

  const cityColClass = showCountryField ? "col-6" : "col-12";

  return (
    <>
      {showCountryField && (
        <div className="col-6">
          <label style={{ color: isDarkMode ? "#e0e0e0" : "#495057" }}>{countryLabel}</label>
          <div className="input-group">
            <span
              className="input-group-text"
              style={{
                backgroundColor: isDarkMode ? "#2d3436" : "#F9F9F9",
                borderColor: isDarkMode ? "#404040" : "#D9D9D9",
                color: isDarkMode ? "#ffffff" : "#6c757d",
              }}
            >
              <i className="fa fa-globe"></i>
            </span>
            <Select
              value={countries.find((c) => c.value === selectedCountry) || null}
              onChange={handleCountryChange}
              options={countries}
              styles={dropdownStyles}
              placeholder="Select Country"
              isClearable={true}
              className="flex-grow-1"
            />
          </div>
        </div>
      )}

      {showCityField && (
        <div className={cityColClass}>
          <label style={{ color: isDarkMode ? "#e0e0e0" : "#495057" }}>{cityLabel}</label>
          <CreatableSelect
            value={
              selectedCity
                ? cities.find((c) =>
                    selectedValueIsLabel ? c.label === selectedCity : c.value === selectedCity
                  ) || { value: selectedCity, label: selectedCity }
                : null
            }
            onChange={handleCityChange}
            options={cities}
            styles={dropdownStyles}
            placeholder={cityPlaceholder}
            isClearable={true}
            isDisabled={!selectedCountry}
            formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
          />
        </div>
      )}
    </>
  );
};

export default CountryCitySelector;
